import type { ReactNode } from "react"
import { Link } from "react-router"
import type { SearchResultItem, TrustView } from "@/lib/api"
import { MarkdownView } from "@/components/console/MarkdownView"
import { Badge } from "@/components/ui/badge"
import { timeAgo } from "@/lib/format"

function isSearchResults(payload: unknown): payload is { results: SearchResultItem[] } {
  if (typeof payload !== "object" || payload === null) return false
  const p = payload as Record<string, unknown>

  // Check for results key that is an array
  if (!("results" in p) || !Array.isArray(p.results)) return false

  // Verify each item has the required fields for a SearchResultItem
  return (p.results as unknown[]).every((item) => {
    if (typeof item !== "object" || item === null) return false
    const obj = item as Record<string, unknown>
    return (
      typeof obj.source === "string" &&
      typeof obj.id === "string" &&
      // score is null for the per-user legs (Graph, personal) -- requiring a
      // number here dropped those results into the raw-JSON fallback.
      (obj.score === null || typeof obj.score === "number") &&
      typeof obj.content === "string"
    )
  })
}

// A minted link is absolute (e.g. {CONSOLE_BASE_URL}/records/{audience}/{id}).
// It's internal to this app only when its origin matches the page's own —
// comparing against a bare path pattern would never match an absolute URL.
function internalPath(url: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url, window.location.origin)
  } catch {
    return null
  }
  if (parsed.origin !== window.location.origin) return null
  return /^\/records\/[^/]+\/[^/]+/.test(parsed.pathname)
    ? parsed.pathname + parsed.search + parsed.hash
    : null
}

const BODY_MAX_LENGTH = 1200

// A result's content is meant to be a passage, not a whole document -- but a
// source can hand back its full raw file, frontmatter included. Strip a leading
// YAML frontmatter block (OKF records carry one) so it isn't rendered as an
// `<hr>`-wrapped key/value dump, then cap the length on a word boundary so one
// oversized SharePoint chunk doesn't dominate the list. The remainder is valid
// Markdown we hand to <MarkdownView>.
function prepareMarkdown(content: string): string {
  const withoutFrontmatter = content.startsWith("---\n")
    ? content.slice(content.indexOf("\n---", 4) + 4).trim()
    : content
  if (withoutFrontmatter.length <= BODY_MAX_LENGTH) return withoutFrontmatter
  const cut = withoutFrontmatter.slice(0, BODY_MAX_LENGTH)
  const lastSpace = cut.lastIndexOf(" ")
  return (lastSpace > BODY_MAX_LENGTH - 120 ? cut.slice(0, lastSpace) : cut).trim() + "…"
}

// Human label for a logical source. Falls back to the raw tag for sources not
// yet enumerated (e.g. future Graph sub-sources), so nothing renders blank.
const SOURCE_LABELS: Record<string, string> = {
  okf: "Curated (OKF)",
  sharepoint: "SharePoint",
  graph: "Microsoft 365",
  personal: "Your notes",
}

function trustBadgeVariant(
  bucket: TrustView["bucket"]
): "default" | "secondary" | "destructive" {
  if (bucket === "high") return "default"
  if (bucket === "medium") return "secondary"
  return "destructive"
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  )
}

// The per-result metadata box -- the search-result echo of the record page's
// sidebar. Renders only the fields a given source actually carries: every
// result has source + relevance; OKF adds the read-time trust view (ADR 0011);
// any source with a resolvable link gets an open-affordance. Teams/Outlook
// (Graph) results slot in here unchanged once live -- they arrive as
// source="graph" with title/url/score and render source + relevance + link.
function ResultMeta({ item }: { item: SearchResultItem }) {
  const internal = item.url ? internalPath(item.url) : null
  const t = item.trust

  return (
    <dl className="space-y-2 font-mono text-xs lg:border-l lg:border-border lg:pl-3">
      <MetaRow label="source">{SOURCE_LABELS[item.source] ?? item.source}</MetaRow>
      {typeof item.score === "number" ? (
        <MetaRow label="relevance">{item.score.toFixed(2)}</MetaRow>
      ) : null}
      {item.audience ? <MetaRow label="audience">{item.audience}</MetaRow> : null}
      {item.your_note ? (
        <MetaRow label="kept">
          <Badge variant="outline">your note</Badge>
          {item.saved_at ? (
            <span className="mt-0.5 block text-muted-foreground">
              saved {timeAgo(item.saved_at)}
            </span>
          ) : null}
        </MetaRow>
      ) : null}
      {item.collection ? (
        <MetaRow label="kind">
          <Badge variant="outline">collection</Badge>
          <span className="mt-0.5 block text-muted-foreground">
            orients, not evidence
            {item.collection.last_update
              ? ` · updated ${timeAgo(item.collection.last_update)}`
              : null}
          </span>
        </MetaRow>
      ) : null}
      {t ? (
        <MetaRow label="trust">
          <span className="flex flex-wrap items-center gap-1">
            <Badge variant={trustBadgeVariant(t.bucket)}>{t.bucket}</Badge>
            <span className="text-muted-foreground">
              {Math.round(t.confidence * 100)}%
            </span>
          </span>
        </MetaRow>
      ) : null}
      {t?.provisional ? (
        <MetaRow label="status">
          <Badge variant="outline">unconfirmed</Badge>
          {item.added_by ? (
            <span className="mt-0.5 block text-muted-foreground">
              added by {item.added_by}
            </span>
          ) : null}
        </MetaRow>
      ) : null}
      {internal ? (
        <MetaRow label="record">
          <Link to={internal} className="text-primary hover:underline">
            open record →
          </Link>
        </MetaRow>
      ) : item.url ? (
        <MetaRow label="link">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            open source →
          </a>
        </MetaRow>
      ) : null}
    </dl>
  )
}

function SearchResultItemCard({ item }: { item: SearchResultItem }) {
  // Display title, falling back to topic_key. Never fall back to id -- it's
  // an opaque source key (a Foundry chunk key, a blob uid, ...), not
  // something a human should ever see as a result's title.
  const displayTitle = item.title ?? item.topic_key ?? "untitled result"
  const internal = item.url ? internalPath(item.url) : null

  const titleEl = internal ? (
    <Link
      to={internal}
      className="font-mono text-sm font-medium text-primary hover:underline"
    >
      {displayTitle}
    </Link>
  ) : item.url ? (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-sm font-medium text-primary hover:underline"
    >
      {displayTitle}
    </a>
  ) : (
    <span className="font-mono text-sm font-medium">{displayTitle}</span>
  )

  return (
    <div className="border border-border bg-background">
      <header className="border-b border-border px-3 py-2">{titleEl}</header>
      <div className="grid gap-3 p-3 lg:grid-cols-[1fr_12rem]">
        {item.content ? (
          <MarkdownView content={prepareMarkdown(item.content)} audience={item.audience} />
        ) : (
          <p className="font-mono text-xs text-muted-foreground">No preview.</p>
        )}
        <ResultMeta item={item} />
      </div>
    </div>
  )
}

export function EvidenceCard({ payload }: { payload: unknown }) {
  if (isSearchResults(payload)) {
    const results = payload.results
    if (results.length === 0) {
      return (
        <p className="text-xs text-muted-foreground">No results in this source.</p>
      )
    }
    return (
      <div className="space-y-2">
        {results.map((item, i) => (
          <SearchResultItemCard key={item.id ?? i} item={item} />
        ))}
      </div>
    )
  }

  // Unrecognized shape: raw dump rather than crashing.
  return (
    <pre className="overflow-x-auto font-mono text-xs">
      {JSON.stringify(payload, null, 2)}
    </pre>
  )
}
